import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import useDocumentKeyEntities from '../../hooks/useDocumentKeyEntities';
import { isPresentableParticipant } from '../../domain/personDisplay';
import EntityDossierModal from '../EntityDossierModal';

const TYPE_LABEL = {
  PERSON: 'person',
  ORGANIZATION: 'org',
  LOCATION: 'place',
  VEHICLE: 'vehicle',
  EVENT: 'event',
  OBJECT: 'object',
  OTHER: 'other',
};

function _label(t) {
  return TYPE_LABEL[t] || (t ? t.toLowerCase() : '');
}

// Dedup by canonical_name (case-insensitive). Upstream sometimes returns
// the same entity twice (e.g., a person mentioned by name and by alias both
// resolving to the same canonical row), which produced "Apple iMac, Apple
// iMac +2" in the row. Dedup at the boundary so other consumers can still
// see the raw list if they want it.
function dedupByName(entities) {
  const seen = new Set();
  const out = [];
  for (const e of entities) {
    const key = (e?.canonical_name || '').trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

// Popover listing every entity for the row — reached via the "+N" overflow so
// the calm two-name strip still leads, but no entity is unreachable. Rendered
// through a portal with a transparent click-catcher overlay (same pattern as
// the dossier modal) so the inventory row's own click handlers don't eat the
// menu-item clicks (the React-18 native-listener gotcha).
function EntityMenu({ rect, entities, onPick, onClose }) {
  return createPortal((
    <div className="ed-menu-overlay" onClick={onClose}>
      <div
        className="ed-menu"
        style={{ top: rect.bottom + 4, left: rect.left }}
        onClick={e => e.stopPropagation()}
      >
        {entities.map(e => (
          <button
            type="button"
            key={e.entity_id || e.canonical_name}
            className="ed-menu-item"
            onClick={() => onPick(e)}
          >
            <span className="ed-menu-name">{e.canonical_name}</span>
            {e.entity_type && <span className="ed-menu-type">{_label(e.entity_type)}</span>}
          </button>
        ))}
      </div>
    </div>
  ), document.body);
}

// Top GraphRAG entities for a doc row. Renders as muted inline text —
// not a pill — so it joins the descriptive metadata strip without
// shouting. Names are clickable → open the entity's cross-document dossier;
// the "+N" overflow opens a menu with every entity.
export default function KeyEntitiesBadge({ docId }) {
  const { caseId } = useParams();
  const { entities, total, loading } = useDocumentKeyEntities(docId);
  // The clicked entity → opens its cross-document dossier (everything the
  // graph knows about that person across the whole case, not just this doc).
  const [dossier, setDossier] = useState(null);
  const [menuRect, setMenuRect] = useState(null);

  if (loading || !entities || entities.length === 0) return null;

  const unique = dedupByName(entities);
  if (unique.length === 0) return null;

  // Lead with real names — generic references ("vehicle", "TEMPUR-PEDIC"
  // object rows, lowercase phrases) stay reachable in the +N menu but
  // shouldn't headline the row. Falls back to upstream order if nothing
  // passes (better an odd name than an empty strip).
  const presentable = unique.filter((e) => isPresentableParticipant(e.canonical_name));
  const head = (presentable.length > 0 ? presentable : unique).slice(0, 2);
  // `total` is the upstream count (with dupes); after dedup the +N should
  // reflect what the user would actually see if we showed them all.
  const extra = Math.max(0, unique.length - head.length);

  const tooltip = unique
    .map(e => `${e.canonical_name}${e.entity_type ? ' (' + _label(e.entity_type) + ')' : ''}`)
    .join(', ');

  function open(e, ent) {
    // Don't let the click select the row / open the doc viewer.
    e.preventDefault();
    e.stopPropagation();
    setMenuRect(null);
    setDossier(ent);
  }

  function toggleMenu(e) {
    e.preventDefault();
    e.stopPropagation();
    setMenuRect(menuRect ? null : e.currentTarget.getBoundingClientRect());
  }

  return (
    <span className="inv-row-entities" title={tooltip}>
      {head.map((e, i) => (
        <span key={e.entity_id || e.canonical_name}>
          {i > 0 && ', '}
          <button
            type="button"
            className="inv-row-entity-link"
            onClick={ev => open(ev, e)}
            title={`Profile: ${e.canonical_name}`}
          >{e.canonical_name}</button>
        </span>
      ))}
      {extra > 0 && (
        <button
          type="button"
          className="inv-row-entities__overflow inv-row-entity-more"
          onClick={toggleMenu}
          title="Show all people & entities"
        > +{extra}</button>
      )}
      {menuRect && (
        <EntityMenu
          rect={menuRect}
          entities={unique}
          onClose={() => setMenuRect(null)}
          onPick={(ent) => { setMenuRect(null); setDossier(ent); }}
        />
      )}
      {dossier && caseId && (
        <EntityDossierModal
          caseId={caseId}
          entityId={dossier.entity_id}
          name={dossier.canonical_name}
          entityType={dossier.entity_type}
          onClose={() => setDossier(null)}
        />
      )}
    </span>
  );
}
